import type { GetBioacousticsSampleDataQuery } from '@/__generated__/graphql.js';

type BirdObservations = GetBioacousticsSampleDataQuery['getBioacousticsSampleData']['birdObservations'];
type DailyData = GetBioacousticsSampleDataQuery['getBioacousticsSampleData']['dailyPollinatorData'];

export interface TopSpeciesEntry {
  commonName: string | null;
  latinTaxa: string;
  count: number;
  mostRecentDate: string;
  audioClip: string;
  imgUrl: string | null;
}

export function hillDiversityQ1(counts: number[]): number {
  const total = counts.reduce((s, c) => s + c, 0);
  if (total === 0 || counts.length === 0) return 0;
  let entropy = 0;
  for (const c of counts) {
    if (c <= 0) continue;
    const p = c / total;
    entropy -= p * Math.log(p);
  }
  return Math.exp(entropy);
}

export function formatLargeNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

export function formatLocalDateTime(isoString: string): string {
  const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return isoString;
  return `${match[1]}-${match[2]}-${match[3]} ${match[4]}:${match[5]}`;
}

export function computeMostRecentDate(dailyData: DailyData): string | null {
  let latest: string | null = null;
  for (const day of dailyData) {
    const d = day.date.substring(0, 10);
    if (latest === null || d > latest) latest = d;
  }
  return latest;
}

export function computeDateRange(mostRecentDate: string | null): { endDate: string; chartCutoff: string } {
  const fmt = (d: Date): string => d.toISOString().substring(0, 10);
  const end = mostRecentDate ? new Date(mostRecentDate) : new Date();
  const cutoff = new Date(end);
  cutoff.setDate(cutoff.getDate() - 14);
  return { endDate: fmt(end), chartCutoff: fmt(cutoff) };
}

export function computeTopSpecies(birdObservations: BirdObservations): TopSpeciesEntry[] {
  const map = new Map<string, TopSpeciesEntry>();
  for (const obs of birdObservations) {
    const key = obs.latinTaxa;
    if (!key) continue;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        commonName: obs.commonName ?? null,
        latinTaxa: obs.latinTaxa,
        count: 1,
        mostRecentDate: obs.dateTime,
        audioClip: obs.audioClip,
        imgUrl: obs.imgUrl ?? null,
      });
    } else {
      existing.count++;
      if (obs.dateTime > existing.mostRecentDate) {
        existing.mostRecentDate = obs.dateTime;
        if (obs.audioClip) existing.audioClip = obs.audioClip;
      }
      if (!existing.imgUrl && obs.imgUrl) existing.imgUrl = obs.imgUrl;
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

export function computeBirdStats(birdObservations: BirdObservations): { diversity: string; species: string; detections: string } {
  const counts = new Map<string, number>();
  for (const obs of birdObservations) {
    if (!obs.latinTaxa) continue;
    counts.set(obs.latinTaxa, (counts.get(obs.latinTaxa) ?? 0) + 1);
  }
  return {
    diversity: counts.size === 0 ? '—' : hillDiversityQ1(Array.from(counts.values())).toFixed(1),
    species: String(counts.size),
    detections: formatLargeNumber(birdObservations.length),
  };
}
